import type { PieceDTO } from '@/features/api/extranet/dto/shared'
import type { AssociationFileDTO } from '@/features/api/extranet/dto/shared/AssociationFileDTO'
import type { NatureConfigDTO } from '@/features/api/extranet/dto/shared/NatureConfigDTO'
import type { Dispositif } from '@/features/domaine/types/opco/shared/Dispositif'
import services from '@/features/services/demande/DemandeAllService'
import {
  associateDocument,
  loadNaturesForDropdown,
  loadNaturesForFile,
} from '@/features/services/detail/shared/DocumentsServices'
import { isNullUndefinedOrEmptyString } from '@/features/shared/tools'
import { computed, ref, shallowRef } from 'vue'
import { ACCEPTED_FILE_TYPES, MAX_FILE_COUNT, MAX_FILE_LENGTH } from '../../demande/shared/components/upload/constants'
import { objectToDropdownItems } from '../../shared/tools/object'
import type { Endpoint } from './UploadFiles.types'

export function useUploadFiles(endpoint: Endpoint) {
  const naturesForDropdown = shallowRef<NatureConfigDTO[]>()
  const naturesForFile = shallowRef<NatureConfigDTO[]>()
  const code = ref<string>()
  const idNatureDuFichier = ref<number>()
  const natureSelectionnée = ref<number>()
  const dispositif = shallowRef<Dispositif | Dispositif[]>()
  const filesList = shallowRef<File[]>([])

  async function inititialize() {
    const cible = endpoint === 'dossiers' && typeof dispositif.value === 'string' ? dispositif.value : 'Facture'
    naturesForDropdown.value = await loadNaturesForDropdown(cible, {
      sablier: true,
      dialogMode: 'retry',
      appCache: true,
    })
    naturesForFile.value = await loadNaturesForFile(endpoint, { sablier: true, dialogMode: 'retry', appCache: true })
  }

  function setState(options: {
    endpoint: Endpoint
    code: string
    fichier?: PieceDTO
    dispositif: Dispositif | Dispositif[]
  }) {
    code.value = options.code
    idNatureDuFichier.value = options.fichier?.idNaturePiece
    dispositif.value = options.dispositif
    filesList.value = []
  }

  const dropDownItems = computed(() => {
    if (!naturesForDropdown.value) return []
    if (!dispositif.value) return []

    const filtered = naturesForDropdown
      .value!.map((elem: NatureConfigDTO) => {
        return elem['intitule']
      })
      .sort((a, b) => a.localeCompare(b))

    filtered.push(filtered.splice(filtered.indexOf('Autre'), 1)[0])

    return objectToDropdownItems(filtered)
  })

  function appendFiles(files: FileList) {
    filesList.value = [...filesList.value, ...Array.from(files)]
  }

  function removeFile(file: File) {
    filesList.value = filesList.value.filter((f) => f !== file)
  }

  const messageErreur = computed(() => {
    if (filesList.value.length > MAX_FILE_COUNT) {
      return `Nombre maximal de fichiers (${MAX_FILE_COUNT}) atteint.`
    }

    for (const file of filesList.value) {
      const fileSizeInMB = file.size / 1024 / 1024
      if (fileSizeInMB > MAX_FILE_LENGTH) {
        return `${file.name}" dépasse la taille maximale de ${MAX_FILE_LENGTH} Mo.`
      }

      if (fileSizeInMB === 0) {
        return `${file.name}" est vide. Veuillez sélectionner un autre fichier.`
      }

      const fileExtension = file.name.split('.').pop()?.toLowerCase()
      if (!ACCEPTED_FILE_TYPES.includes(`.${fileExtension}`)) {
        return `${file.name}" n'est pas un type de fichier accepté (${fileExtension}).`
      }
    }

    return ''
  })

  const findNatureCodeFromDropdown = (natureFichierTemporaire: string) => {
    return naturesForDropdown.value!.find((elem) => elem.intitule === natureFichierTemporaire)?.code || 'Autre'
  }

  const findNatureCodeFromFile = () => {
    return naturesForFile.value!.find((elem) => elem.idsSor[0] === idNatureDuFichier.value)?.code || 'Autre'
  }

  const uploadFiles = async () => {
    let natureFinale = 'Autre'

    if (idNatureDuFichier.value) {
      natureFinale = findNatureCodeFromFile()
    } else if (natureSelectionnée.value !== undefined) {
      natureFinale = findNatureCodeFromDropdown(dropDownItems.value[natureSelectionnée.value].label as any)
    }

    const documents = [] as AssociationFileDTO[]
    for (const file of filesList.value) {
      const id = await services.importFile(file, { dialogMode: 'retry', sablier: true })
      documents.push({ fichierUid: id, nature: natureFinale })
    }
    await associateDocument({ endpoint, code: code.value!, documents })
  }

  const canImport = computed(() => {
    if (messageErreur.value) return false
    if (filesList.value.length === 0) return false
    return isNullUndefinedOrEmptyString(idNatureDuFichier.value) === false || natureSelectionnée.value !== undefined
  })

  return {
    appendFiles,
    code,
    canImport,
    dropDownItems,
    endpoint,
    filesList,
    messageErreur,
    natureSelectionnée,
    inititialize,
    removeFile,
    setState,
    uploadFiles,
  }
}
